import { useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Avatar } from '@components/avatar';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconDepartment } from '@components/icons/department';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconRedo } from '@components/icons/redo';
import { IconRemove } from '@components/icons/remove';
import { IconSend } from '@components/icons/send';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { Menu } from '@components/menu';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { getTranslatedTitle } from '@utils/translated-title';

import { api } from 'api';
import { Loading } from 'components/Loading';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';

import { UserStatus } from './TeamList';

export const TeamDetailsProfile = observer(({ user }) => {
  const { current, t } = useStore();
  const { showModal } = useModal();
  const { hasRolePermission } = usePermissions();

  const departmentsQuery = useQuery({
    queryKey: ['departments', user.id],
    queryFn: () => api.users.loadDepartments(user.id),
    enabled: current.info.isEnterprise,
  });

  if (current.info.isEnterprise && !departmentsQuery.isSuccess) {
    return <Loading />;
  }

  function sendInviteModal(user) {
    showModal('CONFIRM_MODAL', {
      _container: {
        width: '460px',
      },
      confirm: user.sendInvite,
      data: { id: user.id },
      buttonIcon: <IconSend />,
      headerText: t('modals.sendinvite.header'),
      mainText: t('modals.sendinvite.main', user.name),
      confirmText: t('modals.sendinvite.button'),
    });
  }

  return (
    <ProfileContainer>
      <Stack space={6}>
        <Stack space={0} orientation="horizontal" justify="space-between">
          <Stack space={6} orientation="horizontal">
            <Avatar src={user.picture} name={user.name} size="xl" />
            <Stack space={1}>
              <Text font="bold18" text={user.name} />
              <Text text={user.email} />
            </Stack>
          </Stack>
        </Stack>
        <Stack space={0} orientation="horizontal" justify="space-between">
          <UserStatus status={user.status} $font="semibold14">
            <IconStatusIndicator />
            <div>{t(`user.status.${user.status}`)}</div>
          </UserStatus>
          {user.status === 'invited' && hasRolePermission('team:edit-user') && (
            <Button
              variant="secondary"
              iconLeft={<IconRedo />}
              text={t('user.profile.invite.resend')}
              onClick={() => sendInviteModal(user)}
            />
          )}
        </Stack>
        <Separator />
        <Stack space={0}>
          <Text font="semibold14" text={t('user.profile.role.label')} />
          <Text text={t(`user.role.${user.role}`)} />
        </Stack>
        {current.info.isEnterprise && (
          <>
            <Separator />
            <DepartmentList departments={departmentsQuery.data} user={user} />
          </>
        )}
      </Stack>
    </ProfileContainer>
  );
});

const DepartmentList = ({ departments, user }) => {
  const { current, t, lang } = useStore();
  const { showModal } = useModal();
  const { hasRolePermission } = usePermissions();

  // We need this query to find out if the logged in user
  // has edit/remove access for each department
  const allDepartmentsQuery = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.departments.list(),
    enabled: current.info.isEnterprise,
  });

  if (!allDepartmentsQuery.isSuccess) {
    return <Loading />;
  }

  if (departments.length === 0) {
    return (
      <Stack orientation="horizontal" space={0} justify="space-between">
        <Stack space={0}>
          <Text font="semibold14" text={t('user.profile.departments.label')} />
          <Text color="purple60" text={t('user.profile.departments.none')} />
        </Stack>
        {hasRolePermission('team:edit-user') && (
          <Stack space={0} align="flex-end">
            <Button
              iconLeft={<IconAddPlus />}
              variant="secondary"
              text={t('user.profile.departments.add')}
              onClick={() => showModal('ADD_USER_TO_DEPARTMENT', { userId: user.id })}
            />
          </Stack>
        )}
      </Stack>
    );
  }
  return (
    <Stack space={4}>
      <Text font="semibold14" text={t('user.profile.departments.label')} />
      {departments.map((userDepartment) => {
        const department = allDepartmentsQuery.data.find(
          (department) => department.id === userDepartment.departmentId,
        );
        return (
          <DepartmentCard
            key={userDepartment.departmentId}
            department={userDepartment}
            departmentTitle={getTranslatedTitle(
              department.translations,
              lang,
              current.info.defaultLanguage,
            )}
            user={user}
            canEditFullAccess={department.canEditFullAccess}
            hasAccess={department.hasAccess}
          />
        );
      })}
      {hasRolePermission('team:edit-user') && (
        <Stack space={0} align="flex-end">
          <Button
            iconLeft={<IconAddPlus />}
            variant="secondary"
            text={t('user.profile.departments.add')}
            onClick={() => showModal('ADD_USER_TO_DEPARTMENT', { userId: user.id })}
          />
        </Stack>
      )}
    </Stack>
  );
};

const DepartmentCard = ({ department, departmentTitle, user, canEditFullAccess, hasAccess }) => {
  const { t } = useStore();
  const { showModal } = useModal();
  return (
    <Card interactive space={4}>
      <Stack space={0} orientation="horizontal" justify="space-between" align="center">
        <Stack space={4} orientation="horizontal" align="center">
          <IconDepartment color="elementPurple80" />
          <Stack space={0}>
            <Text font="semibold14" text={departmentTitle} />
            <Stack space={1} orientation="horizontal">
              <Text
                font="regular14"
                color="textSecondary"
                text={t('user.profile.departments.fullaccess')}
              />
              <Text font="regular14" color="textSecondary" text="•" />
              <Text
                font="regular14"
                color="textSecondary"
                text={department.fullAccess ? t('common.yes') : t('common.no')}
              />
            </Stack>
          </Stack>
        </Stack>
        <Menu buttonProps={{ variant: 'ghost', iconLeft: <IconMoreVertical /> }}>
          <Menu.Item
            onSelect={() =>
              showModal('EDIT_DEPARTMENT_USER', {
                departmentId: department.departmentId,
                userId: user.id,
                departmentTitle,
                fullAccess: department.fullAccess,
                canEditFullAccess,
              })
            }
            label={t('user.profile.departments.edit')}
            icon={<IconEdit />}
            isDisabled={!hasAccess}
          />
          <Menu.Item
            onSelect={() =>
              showModal('DELETE_DEPARTMENT_USER', {
                departmentId: department.departmentId,
                userId: user.id,
                departmentTitle,
                userName: user.name,
              })
            }
            variant="danger"
            label={t('user.profile.departments.remove')}
            icon={<IconRemove />}
            isDisabled={!hasAccess || (!canEditFullAccess && department.fullAccess)}
          />
        </Menu>
      </Stack>
    </Card>
  );
};

const ProfileContainer = styled.div(() => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  '@media(max-width: 1200px)': {
    gridTemplateColumns: '2fr 1fr',
  },
  '@media(max-width: 900px)': {
    gridTemplateColumns: '1fr',
  },
}));
